"use client";

import { useMemo } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { useTranslation } from 'react-i18next';
import { useMultipleIndividualFormFields } from "@/hooks";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import FlowMapAppearanceEditor from "@/components/dashboard/widgets/flow-map/flow-map-appearance-editor";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormAutocomplete } from "@/components/widget-builder/form-autocomplete";

interface FlowMapMetric {
  formId: string;
  region: string;
  city1FieldId: string;
  city2FieldId: string;
  valueFieldId: string;
  primaryCityIndicator: {
    fieldId: string;
    value: string;
  };
}

interface FlowMapAppearance {
  showLegend?: boolean;
  legendLabel?: string;
  legendLocation?: "top-left" | "top-center" | "top-right" | "bottom-left" | "bottom-center" | "bottom-right";
  primaryCityColor?: string;
  secondaryCityColor?: string;
  lineColor?: string;
  showCityName?: boolean;
  primaryCityNameColor?: string;
  secondaryCityNameColor?: string;
  showValueTooltip?: boolean;
  countryFillColor?: string;
  countryHoverColor?: string;
}

interface FlowMapConfigValue {
  metric: FlowMapMetric;
  filters: any[];
  appearance: FlowMapAppearance;
}

interface FlowMapConfigPanelProps {
  value: FlowMapConfigValue;
  onChange: (val: FlowMapConfigValue) => void;
  forms: any[];
}

export default function FlowMapConfigPanel({ value, onChange, forms }: FlowMapConfigPanelProps) {
  const { t } = useTranslation();
  const metricFormId = value.metric?.formId || "";
  const { formFieldsMap } = useMultipleIndividualFormFields(metricFormId ? [metricFormId] : []);

  const updateMetric = (updates: Partial<FlowMapMetric>) => {
    onChange({
      ...value,
      metric: { ...value.metric, ...updates } as FlowMapMetric,
    });
  };

  const fields = formFieldsMap[metricFormId] || [];

  // Filter fields for city (text type) and value (number type)
  const cityFields = fields.filter((f: any) =>
    f.type === "text" || f.type === "Text" || f.type === "ShortText" || f.type === "LongText"
  );
  const valueFields = fields.filter((f: any) =>
    f.type === "number" || f.type === "Number" || f.type === "Decimal"
  );

  // Get primary city indicator field
  const primaryIndicatorField = fields.find((f: any) => f.id === value.metric?.primaryCityIndicator?.fieldId);
  const isSelectField = primaryIndicatorField && ["select", "Dropdown"].includes(primaryIndicatorField.type);
  const selectOptions = primaryIndicatorField?.options || [];

  // Get value field label for default legend label
  const valueField = valueFields.find((f: any) => f.id === value.metric?.valueFieldId);
  const defaultLegendLabel = valueField?.label || "Value";

  const appearance = value.appearance || {
    showLegend: false,
    legendLabel: defaultLegendLabel,
    legendLocation: "bottom-center",
    primaryCityColor: "#ef4444",
    secondaryCityColor: "#06b6d4",
    lineColor: "#3b82f6",
    showCityName: true,
    primaryCityNameColor: "#ffffff",
    secondaryCityNameColor: "#ffffff",
    showValueTooltip: true,
    showArrowHead: false,
    countryFillColor: "#e5e7eb",
    countryHoverColor: "#d1d5db",
    countryBorderColor: "#d1d5db",
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('processManagement.metrics')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label>{t('processManagement.form')}</Label>
              <FormAutocomplete
                value={value.metric?.formId || ""}
                onValueChange={(formId) => updateMetric({
                  formId: formId || "",
                  city1FieldId: "",
                  city2FieldId: "",
                  valueFieldId: "",
                  primaryCityIndicator: { fieldId: "", value: "" }
                })}
                forms={forms.map((f: any) => ({
                  id: f.id,
                  title: f.title || f.name || "",
                  name: f.name || f.title || "",
                  description: f.description,
                }))}
                placeholder={t('processManagement.selectFormPlaceholder')}
              />
            </div>
            <div>
              <Label>Map Region</Label>
              <Select
                value={value.metric?.region || "africa"}
                onValueChange={(region) => updateMetric({ region })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="africa">Africa</SelectItem>
                  <SelectItem value="world" disabled>World (Coming Soon)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>City 1 Field</Label>
              <Select
                value={value.metric?.city1FieldId || ""}
                onValueChange={(city1FieldId) => updateMetric({ city1FieldId })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select city 1 field" />
                </SelectTrigger>
                <SelectContent>
                  {cityFields.map((f: any) => (
                    <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>City 2 Field</Label>
              <Select
                value={value.metric?.city2FieldId || ""}
                onValueChange={(city2FieldId) => updateMetric({ city2FieldId })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select city 2 field" />
                </SelectTrigger>
                <SelectContent>
                  {cityFields.map((f: any) => (
                    <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label>{t('processManagement.valueField')}</Label>
              <Select
                value={value.metric?.valueFieldId || ""}
                onValueChange={(valueFieldId) => updateMetric({ valueFieldId })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('processManagement.selectValueField')} />
                </SelectTrigger>
                <SelectContent>
                  {valueFields.map((f: any) => (
                    <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Primary City Indicator Field</Label>
              <Select
                value={value.metric?.primaryCityIndicator?.fieldId || ""}
                onValueChange={(fieldId) => updateMetric({
                  primaryCityIndicator: {
                    fieldId,
                    value: ""
                  }
                })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select indicator field" />
                </SelectTrigger>
                <SelectContent>
                  {fields.map((f: any) => (
                    <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {value.metric?.primaryCityIndicator?.fieldId && (
              <div>
                <Label>Primary City Indicator Value</Label>
                {isSelectField ? (
                  <Select
                    value={value.metric?.primaryCityIndicator?.value || ""}
                    onValueChange={(val) => updateMetric({
                      primaryCityIndicator: {
                        ...value.metric.primaryCityIndicator,
                        value: val
                      }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select value" />
                    </SelectTrigger>
                    <SelectContent>
                      {selectOptions.map((opt: any) => {
                        const optValue = typeof opt === 'string' ? opt : (opt.value || opt.label || opt);
                        return (
                          <SelectItem key={optValue} value={String(optValue)}>
                            {typeof opt === 'string' ? opt : (opt.label || opt.value || opt)}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input
                    type="text"
                    value={value.metric?.primaryCityIndicator?.value || ""}
                    onChange={(e) => updateMetric({
                      primaryCityIndicator: {
                        ...value.metric.primaryCityIndicator,
                        value: e.target.value
                      }
                    })}
                    placeholder="Enter indicator value"
                  />
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t('processManagement.appearance')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <FlowMapAppearanceEditor
            appearance={appearance}
            defaultLegendLabel={defaultLegendLabel}
            onChange={(app) => onChange({ ...value, appearance: app })}
          />
        </CardContent>
      </Card>
    </div>
  );
}

